import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import ConfirmationModal from './components/ConfirmationModal';

const SuperAdminCreatePlan = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState({
    planName: '',
    planDescription: '',
    monthlyPrice: '',
    yearlyPrice: '',
    trialDays: '',
    maxReportsLimit: '',
    aiUsageLimit: '',
    features: {
      bmcAccess: false,
      predictiveAnalysis: false,
      goalIntelligence: false,
      exportReports: false,
      prioritySupport: false
    },
    teamMemberLimit: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFeatureToggle = (feature) => {
    setFormData({
      ...formData,
      features: {
        ...formData?.features,
        [feature]: !formData?.features?.[feature]
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.planName?.trim()) {
      newErrors.planName = 'Plan name is required';
    }

    if (!formData?.planDescription?.trim()) {
      newErrors.planDescription = 'Plan description is required';
    }

    if (!formData?.monthlyPrice || isNaN(formData?.monthlyPrice) || Number(formData?.monthlyPrice) < 0) {
      newErrors.monthlyPrice = 'Valid monthly price is required';
    }

    if (!formData?.yearlyPrice || isNaN(formData?.yearlyPrice) || Number(formData?.yearlyPrice) < 0) {
      newErrors.yearlyPrice = 'Valid yearly price is required';
    }

    if (!formData?.trialDays || isNaN(formData?.trialDays) || Number(formData?.trialDays) < 0) {
      newErrors.trialDays = 'Valid trial days is required';
    }

    if (!formData?.maxReportsLimit || isNaN(formData?.maxReportsLimit)) {
      newErrors.maxReportsLimit = 'Valid reports limit is required';
    }

    if (!formData?.aiUsageLimit || isNaN(formData?.aiUsageLimit)) {
      newErrors.aiUsageLimit = 'Valid AI usage limit is required';
    }

    if (!formData?.teamMemberLimit || isNaN(formData?.teamMemberLimit) || Number(formData?.teamMemberLimit) < 1) {
      newErrors.teamMemberLimit = 'Valid team member limit is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmSave = () => {
    setIsSubmitting(true);

    setTimeout(() => {
      console.log('Plan created:', formData);
      setIsSubmitting(false);
      setShowConfirmModal(false);
      handleCancel();
      window.location.href = '/super-admin-all-plans';
    }, 1000);
  };

  const handleCancel = () => {
    setFormData({
      planName: '',
      planDescription: '',
      monthlyPrice: '',
      yearlyPrice: '',
      trialDays: '',
      maxReportsLimit: '',
      aiUsageLimit: '',
      features: {
        bmcAccess: false,
        predictiveAnalysis: false,
        goalIntelligence: false,
        exportReports: false,
        prioritySupport: false
      },
      teamMemberLimit: '',
      isActive: true
    });
    setErrors({});
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
              <Icon name="Plus" size={32} className="text-primary" />
              Create Subscription Plan
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
              Configure a new subscription plan with pricing, features, and limits
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="FileText" size={20} className="text-primary" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="Plan Name"
                    type="text"
                    value={formData?.planName}
                    onChange={(e) => handleInputChange('planName', e?.target?.value)}
                    placeholder="e.g., Growth Plan"
                    required
                    error={errors?.planName}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Plan Description <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    value={formData?.planDescription}
                    onChange={(e) => handleInputChange('planDescription', e?.target?.value)}
                    placeholder="Describe the plan benefits and target audience..."
                    rows={4}
                    className={`w-full px-3 py-2 bg-background border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                      errors?.planDescription ? 'border-destructive' : 'border-border'
                    }`}
                  />
                  {errors?.planDescription && (
                    <p className="text-sm text-destructive mt-1">{errors?.planDescription}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="DollarSign" size={20} className="text-primary" />
                Pricing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Monthly Price (₹)"
                  type="number"
                  value={formData?.monthlyPrice}
                  onChange={(e) => handleInputChange('monthlyPrice', e?.target?.value)}
                  placeholder="2999"
                  required
                  error={errors?.monthlyPrice}
                />
                <Input
                  label="Yearly Price (₹)"
                  type="number"
                  value={formData?.yearlyPrice}
                  onChange={(e) => handleInputChange('yearlyPrice', e?.target?.value)}
                  placeholder="29990"
                  required
                  error={errors?.yearlyPrice}
                />
                <Input
                  label="Trial Days"
                  type="number"
                  value={formData?.trialDays}
                  onChange={(e) => handleInputChange('trialDays', e?.target?.value)}
                  placeholder="14"
                  required
                  error={errors?.trialDays}
                />
              </div>
            </div>

            {/* Limits */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Gauge" size={20} className="text-primary" />
                Usage Limits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Max Reports Limit"
                  type="number"
                  value={formData?.maxReportsLimit}
                  onChange={(e) => handleInputChange('maxReportsLimit', e?.target?.value)}
                  placeholder="50"
                  description="-1 for unlimited"
                  required
                  error={errors?.maxReportsLimit}
                />
                <Input
                  label="AI Usage Limit"
                  type="number"
                  value={formData?.aiUsageLimit}
                  onChange={(e) => handleInputChange('aiUsageLimit', e?.target?.value)}
                  placeholder="100"
                  description="-1 for unlimited"
                  required
                  error={errors?.aiUsageLimit}
                />
                <Input
                  label="Team Member Limit"
                  type="number"
                  value={formData?.teamMemberLimit}
                  onChange={(e) => handleInputChange('teamMemberLimit', e?.target?.value)}
                  placeholder="5"
                  required
                  error={errors?.teamMemberLimit}
                />
              </div>
            </div>

            {/* Feature Access */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Sparkles" size={20} className="text-primary" />
                Feature Access
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Checkbox
                  checked={formData?.features?.bmcAccess}
                  onChange={() => handleFeatureToggle('bmcAccess')}
                  label="BMC Access"
                  description="Enable Business Model Canvas module"
                />
                <Checkbox
                  checked={formData?.features?.predictiveAnalysis}
                  onChange={() => handleFeatureToggle('predictiveAnalysis')}
                  label="Predictive Analysis Access"
                  description="Enable predictive analytics features"
                />
                <Checkbox
                  checked={formData?.features?.goalIntelligence}
                  onChange={() => handleFeatureToggle('goalIntelligence')}
                  label="Goal Intelligence Access"
                  description="Enable goal planning and tracking"
                />
                <Checkbox
                  checked={formData?.features?.exportReports}
                  onChange={() => handleFeatureToggle('exportReports')}
                  label="Export Reports"
                  description="Allow users to export reports"
                />
                <Checkbox
                  checked={formData?.features?.prioritySupport}
                  onChange={() => handleFeatureToggle('prioritySupport')}
                  label="Priority Support"
                  description="Provide priority customer support"
                />
              </div>
            </div>

            {/* Plan Status */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Settings" size={20} className="text-primary" />
                Plan Status
              </h3>
              <Checkbox
                checked={formData?.isActive}
                onChange={() => setFormData({ ...formData, isActive: !formData?.isActive })}
                label="Active"
                description="Make this plan available for subscription immediately"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                iconName="X"
                iconPosition="left"
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName="Save"
                iconPosition="left"
                fullWidth
                loading={isSubmitting}
              >
                Save Plan
              </Button>
            </div>
          </form>

          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={showConfirmModal}
            planName={formData?.planName}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={confirmSave}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default SuperAdminCreatePlan;